import React, { useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";

type ViewerProps = {
    fileBlob: Blob;
    fileName: string;
};

const isTxt = (name: string) => name.toLowerCase().endsWith(".txt");

const Viewer = React.memo(({ fileBlob, fileName }: ViewerProps) => {
    const [url, setUrl] = useState<string>("");
    const [text, setText] = useState<string>("");

    const txt = useMemo(() => isTxt(fileName), [fileName]);

    useEffect(() => {
        // Clean previous state
        setUrl("");
        setText("");

        if (txt) {
            // Read as text and render ourselves
            fileBlob
                .text()
                .then(setText)
                .catch(() => setText("Failed to load text file."));
            return;
        }

        // For non-text, use object URL + DocViewer
        const objectUrl = URL.createObjectURL(fileBlob);
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [fileBlob, txt]);

    if (txt) {
        if (!text) return null;
        return (
            <pre
                style={{
                    width: "100%",
                    height: "100%",
                    margin: 0,
                    padding: 12,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: 13,
                    lineHeight: 1.5,
                }}
            >
                {text}
            </pre>
        );
    }

    if (!url) return null;

    return (
        <DocViewer
            style={{ width: "100%", height: "100%" }}
            pluginRenderers={DocViewerRenderers}
            documents={[{ uri: url, fileName }]}
            config={{
                header: { disableFileName: true, disableHeader: true },
                pdfZoom: { defaultZoom: 0.5, zoomJump: 0.1 },
            }}
        />
    );
}, (prev, next) => prev.fileBlob === next.fileBlob && prev.fileName === next.fileName);

export default Viewer;
